import { useEffect, useState, useCallback } from 'react';
import { PageContainer } from '@/components/common/PageContainer';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Search, Truck, Star, Link2, Link2Off, Eye } from 'lucide-react';
import { Carrier, CARRIER_TYPE_LABELS, CarrierType } from '@/types/carrier.types';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { CarrierInfoModal } from '@/components/features/carriers/CarrierInfoModal';

interface CarrierConnection {
  id: string;
  carrier_id: string;
  is_default: boolean;
  is_active: boolean;
  connected_at: string;
  carrier: Carrier;
}

export default function TenantCarriersPage() {
  const { user } = useAuth();
  const { get, post, patch, delete: del, loading } = useApi();

  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [connections, setConnections] = useState<CarrierConnection[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string | null>(null);

  const connectionMap = Object.fromEntries(
    connections.map((c) => [c.carrier_id, c]),
  );

  const fetchData = useCallback(async () => {
    const [carriersRes, connectionsRes] = await Promise.all([
      get('/api/v1/carriers', { is_active: true, limit: 100 }),
      get('/api/v1/tenants/my/carriers'),
    ]);

    if (carriersRes?.list) setCarriers(carriersRes.list);
    else if (Array.isArray(carriersRes)) setCarriers(carriersRes);

    if (Array.isArray(connectionsRes)) setConnections(connectionsRes);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConnect = async (carrierId: string) => {
    setActionLoading(carrierId);
    try {
      const res: any = await post('/api/v1/tenants/my/carriers', { carrier_id: carrierId });
      if (res?.success !== false) {
        toast.success('تم التعاقد مع شركة الشحن بنجاح');
        setSelectedCarrierId(null);
        await fetchData();
      } else {
        toast.error(res?.message || 'فشل التعاقد');
      }
    } catch {
      toast.error('فشل التعاقد');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetDefault = async (connectionId: string) => {
    setActionLoading(connectionId);
    try {
      const res: any = await patch(`/api/v1/tenants/my/carriers/${connectionId}`, { is_default: true });
      if (res?.success !== false) {
        toast.success('تم تعيين الناقل كافتراضي');
        await fetchData();
      } else {
        toast.error(res?.message || 'فشل التعيين');
      }
    } catch {
      toast.error('فشل التعيين');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    setActionLoading(connectionId);
    try {
      const res: any = await del(`/api/v1/tenants/my/carriers/${connectionId}`);
      if (res?.success !== false) {
        toast.success('تم إلغاء التعاقد');
        await fetchData();
      } else {
        toast.error(res?.message || 'فشل إلغاء التعاقد');
      }
    } catch {
      toast.error('فشل إلغاء التعاقد');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = carriers.filter((c) =>
    !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (loading && carriers.length === 0) {
    return (
      <PageContainer>
        <LoadingSkeleton type="card" rows={6} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-4 md:space-y-6" dir="rtl">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">شركات الشحن</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            تصفح شركات الشحن المتاحة وتعاقد معها
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="ابحث عن شركة شحن..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pe-10"
          />
        </div>

        {/* Stats */}
        {connections.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link2 className="h-4 w-4 text-green-500" />
            <span>متعاقد مع <strong className="text-foreground">{connections.length}</strong> شركة</span>
          </div>
        )}

        {/* Carriers Grid */}
        {filtered.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">لا توجد شركات شحن</h3>
              <p className="text-muted-foreground">لم يتم العثور على شركات شحن متاحة.</p>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((carrier) => {
              const connection = connectionMap[carrier.id];
              const isConnected = !!connection;
              const isLoading =
                actionLoading === carrier.id || actionLoading === connection?.id;

              return (
                <Card
                  key={carrier.id}
                  className={`p-5 transition-shadow hover:shadow-md cursor-pointer ${
                    isConnected ? 'border-green-500/30 bg-green-500/5' : ''
                  }`}
                  onClick={() => setSelectedCarrierId(carrier.id)}
                >
                  {/* Carrier Identity */}
                  <div className="flex items-start gap-3 mb-4">
                    {carrier.logo_url ? (
                      <img
                        src={carrier.logo_url}
                        alt={carrier.name}
                        className="h-12 w-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="p-2.5 bg-primary/10 rounded-lg flex-shrink-0">
                        <Truck className="h-6 w-6 text-primary" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-base leading-tight">{carrier.name}</h3>
                        {isConnected && connection.is_default && (
                          <Badge className="gap-1 bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs">
                            <Star className="h-3 w-3 fill-current" />
                            افتراضي
                          </Badge>
                        )}
                        {isConnected && (
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-xs">
                            متعاقد
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  {carrier.contact_info?.email && (
                    <p className="text-xs text-muted-foreground mb-3 truncate">
                      {carrier.contact_info.email}
                    </p>
                  )}

                  {/* Payout Info */}
                  {carrier.is_guaranteed_payout && (
                    <p className="text-xs text-blue-500 mb-3">
                      دفع مضمون خلال {carrier.payout_duration_in_hours} ساعة
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto pt-2 border-t">
                    {isConnected ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          disabled={connection.is_default || isLoading}
                          onClick={(e) => { e.stopPropagation(); handleSetDefault(connection.id); }}
                        >
                          <Star className="h-3.5 w-3.5 ms-1" />
                          تعيين كافتراضي
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/60"
                          disabled={isLoading}
                          onClick={(e) => { e.stopPropagation(); handleDisconnect(connection.id); }}
                        >
                          <Link2Off className="h-3.5 w-3.5 ms-1" />
                          الغاء التعاقد
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={(e) => { e.stopPropagation(); setSelectedCarrierId(carrier.id); }}
                      >
                        <Eye className="h-3.5 w-3.5 ms-1" />
                        عرض التفاصيل
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Carrier Info Modal */}
      <CarrierInfoModal
        open={!!selectedCarrierId}
        carrierId={selectedCarrierId}
        isConnected={!!selectedCarrierId && !!connectionMap[selectedCarrierId]}
        onClose={() => setSelectedCarrierId(null)}
        onConnect={handleConnect}
        connectLoading={!!actionLoading}
      />
    </PageContainer>
  );
}
